import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { parseISO } from "date-fns";
import React from "react";

import { formatIsoDate, formatIsoTime } from "../common/dateUtils";
import { getEventTitle } from "../common/utils";

const cardHeight = 555;
const cardVerticalPadding = 28;
const firstPageColumnCount = 2;
const secondPageColumnCount = 3;
const firstPageColumnGap = 10;
const secondPageColumnGap = 10;
const firstPageColumnHeight = cardHeight - cardVerticalPadding;
const secondPageColumnHeight = cardHeight - cardVerticalPadding;
const eventVerticalMargin = 10;
const eventBaseHeight = 34;
const extraTitleLineHeight = 12;
const secondaryLineHeight = 12;
const titleCharsPerLine = 20;
const secondaryCharsPerLine = 24;

const styles = StyleSheet.create({
	container: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
	},
	title: {
		padding: "17px 10px",
		fontSize: 15,
		color: "#00368e",
	},
	card: {
		width: 340,
		height: cardHeight,
		padding: "12px 12px 16px 12px",
		borderRadius: 4,
		borderColor: "#e8eaf0",
		borderWidth: 1,
		borderStyle: "solid",
		backgroundColor: "#ffffff",
	},
	section: {
		marginTop: 10,
		marginBottom: 10,
	},
	sectionTitle: {
		color: "#00368e",
		fontSize: 14,
		fontWeight: 600,
		paddingBottom: 10,
	},
	bold: {
		color: "black",
		fontSize: 12,
		fontWeight: 600,
		maxWidth: 138,
	},
	label: {
		color: "#9ba0b1",
		fontSize: 12,
	},
	value: {
		fontSize: 12,
	},

	licenseInfoContainer: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		width: 360,
	},

	infoItem: {
		padding: "6px 6px 6px 0",
		width: 150,
	},

	indCard: {
		width: 160,
		borderRadius: 4,
		borderColor: "#e8eaf0",
		borderWidth: 2,
		borderStyle: "solid",
		backgroundColor: "#ffffff",
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
	},
	indStatus: {
		minHeight: 34,
		width: 6,
		borderTopLeftRadius: 3,
		borderBottomLeftRadius: 3,
		marginRight: 8,
	},
	indContent: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
		flexShrink: 1,
		padding: "6px 8px 6px 0",
	},
	secondaryValue: {
		fontSize: 10,
		color: "#9ba0b1",
		marginTop: 2,
		maxWidth: 138,
	},
	eventsContainer: {
		display: "flex",
		flexDirection: "column",
	},
	firstPageEventsContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
	},
	firstPageEventsColumn: {
		display: "flex",
		flexDirection: "column",
		width: 255,
	},
	firstPageEventsColumnSpacer: {
		width: firstPageColumnGap,
	},
	multiColumnEventsContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
	},
	multiColumnEventsColumn: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},
	multiColumnEventsColumnSpacer: {
		width: secondPageColumnGap,
	},
});

const getStatusColor = status => {
	switch (status) {
		case -1:
		case 6: {
			return "#00368e";
		}
		case 1:
		case 4:
		case 3:
		case 5: {
			return "#ffd740";
		}
		case 2: {
			return "#81c784";
		}
		case 0: {
			return "#e64a19";
		}
		default: {
			return "#9ba0b1";
		}
	}
};

const IndicatorCard = ({ status, text, secondaryText }) => (
	<View style={styles.indCard}>
		<View style={{ ...styles.indStatus, backgroundColor: getStatusColor(status) }} />
		<View style={styles.indContent}>
			<Text style={styles.bold}>{text}</Text>
			{secondaryText && <Text style={styles.secondaryValue}>{secondaryText}</Text>}
		</View>
	</View>
);

const adminUserNameMetadataPrefix = "adminUserName:";

const getAdminUserNameFromMetadata = metadataAttributes =>
	Array.isArray(metadataAttributes)
		? metadataAttributes
				.find(
					attribute =>
						typeof attribute === "string" &&
						attribute.startsWith(adminUserNameMetadataPrefix)
				)
				?.slice(adminUserNameMetadataPrefix.length) || null
		: null;

const getWrappedLineCount = (text, charsPerLine) => {
	if (!text) {
		return 0;
	}

	return Math.max(1, Math.ceil(String(text).length / charsPerLine));
};

const DriverFilePdf = ({ driver, includeUvv, translate }) => {
	const getFsStatusText = status => {
		switch (status) {
			case 1:
			case 4:
			case 3:
			case 5: {
				return translate("drivers.events.fsPending");
			}
			case 2: {
				return translate("drivers.events.fsDone");
			}
			case 0: {
				return translate("drivers.events.fsFail");
			}
			//
			// case 6:
			default: {
				return translate("drivers.events.newDriver");
			}
		}
	};

	const displayEvents = driver.Events
		? [...driver.Events].filter(e => !e.text || e.text !== "retry")
		: [];
	displayEvents.sort((a, b) => parseISO(b.createdAt) - parseISO(a.createdAt));

	const renderEvent = event => {
		const adminUserName = getAdminUserNameFromMetadata(event.metadataAttributes);
		const secondaryText = adminUserName
			? translate("drivers.events.adminUserName", {
					user: adminUserName,
				})
			: null;

		return (
			<View
				key={event.id}
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "flex-start",
					margin: 5,
				}}
			>
				<View
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-end",
						marginRight: 10,
						marginTop: 6,
					}}
				>
					<Text style={styles.value}>{formatIsoDate(event.createdAt)}</Text>
					<Text style={styles.label}>{formatIsoTime(event.createdAt)}</Text>
				</View>
				<View>
					<IndicatorCard
						status={event.status}
						text={getEventTitle(event.eventType, event.metadataAttributes, translate)}
						secondaryText={secondaryText}
					/>
				</View>
			</View>
		);
	};

	const getEventHeight = event => {
		const adminUserName = getAdminUserNameFromMetadata(event.metadataAttributes);
		const title = getEventTitle(event.eventType, event.metadataAttributes, translate);
		const secondaryText = adminUserName
			? translate("drivers.events.adminUserName", {
					user: adminUserName,
				})
			: null;
		const titleLineCount = getWrappedLineCount(title, titleCharsPerLine);
		const secondaryLineCount = getWrappedLineCount(secondaryText, secondaryCharsPerLine);

		return (
			eventBaseHeight +
			Math.max(0, titleLineCount - 1) * extraTitleLineHeight +
			secondaryLineCount * secondaryLineHeight +
			eventVerticalMargin
		);
	};

	const splitFirstPageEvents = events => {
		const firstPageColumns = Array.from({ length: firstPageColumnCount }, () => []);
		const columnHeights = Array.from({ length: firstPageColumnCount }, () => 0);
		let currentColumnIndex = 0;
		let renderedEventCount = 0;

		for (const event of events) {
			const nextHeight = getEventHeight(event);

			while (
				currentColumnIndex < firstPageColumnCount &&
				columnHeights[currentColumnIndex] + nextHeight > firstPageColumnHeight
			) {
				currentColumnIndex += 1;
			}

			if (currentColumnIndex >= firstPageColumnCount) {
				break;
			}

			firstPageColumns[currentColumnIndex].push(event);
			columnHeights[currentColumnIndex] += nextHeight;
			renderedEventCount += 1;
		}

		return {
			firstPageColumns,
			renderedEventCount,
		};
	};

	const splitEventsIntoColumns = (events, columnCount, columnHeight) => {
		const columns = Array.from({ length: columnCount }, () => []);
		const columnHeights = Array.from({ length: columnCount }, () => 0);
		let currentColumnIndex = 0;
		let renderedEventCount = 0;

		for (const event of events) {
			const nextHeight = getEventHeight(event);

			while (
				currentColumnIndex < columnCount &&
				columnHeights[currentColumnIndex] + nextHeight > columnHeight
			) {
				currentColumnIndex += 1;
			}

			if (currentColumnIndex >= columnCount) {
				break;
			}

			columns[currentColumnIndex].push(event);
			columnHeights[currentColumnIndex] += nextHeight;
			renderedEventCount += 1;
		}

		return {
			columns,
			renderedEventCount,
		};
	};

	const { firstPageColumns, renderedEventCount: firstPageEventCount } =
		splitFirstPageEvents(displayEvents);
	const followingPages = [];
	let remainingEvents = displayEvents.slice(firstPageEventCount);

	while (remainingEvents.length > 0) {
		const { columns, renderedEventCount } = splitEventsIntoColumns(
			remainingEvents,
			secondPageColumnCount,
			secondPageColumnHeight
		);

		if (renderedEventCount === 0) {
			break;
		}

		followingPages.push(columns);
		remainingEvents = remainingEvents.slice(renderedEventCount);
	}

	const renderEvents = events => (
		<View style={styles.eventsContainer}>{events.map(event => renderEvent(event))}</View>
	);

	const renderFirstPageEvents = columns => (
		<View style={styles.firstPageEventsContainer}>
			<View style={styles.firstPageEventsColumn}>
				{columns[0].map(event => renderEvent(event))}
			</View>
			<View style={styles.firstPageEventsColumnSpacer} />
			<View style={styles.firstPageEventsColumn}>
				{columns[1].map(event => renderEvent(event))}
			</View>
		</View>
	);

	const renderMultiColumnEvents = columns => (
		<View style={styles.multiColumnEventsContainer}>
			{columns.map((column, index) => (
				<React.Fragment key={`column-${index}`}>
					<View style={styles.multiColumnEventsColumn}>
						{column.map(event => renderEvent(event))}
					</View>
					{index < columns.length - 1 && (
						<View style={styles.multiColumnEventsColumnSpacer} />
					)}
				</React.Fragment>
			))}
		</View>
	);

	return (
		<Document>
			<Page orientation="landscape" size="A4">
				<View style={styles.container}>
					<View style={styles.card}>
						<View style={styles.section}>
							<Text style={styles.sectionTitle}>
								{translate("drivers.sections.contactInfo")}
							</Text>
							<Text
								style={styles.value}
							>{`${driver.firstName}  ${driver.surName}`}</Text>
							<Text style={styles.value}>{driver.email}</Text>
							{driver.driverTypeEnum !== null && (
								<Text style={styles.value}>
									{translate(`drivers.driverTypes.${driver.driverTypeEnum}`)}
								</Text>
							)}
						</View>

						<View style={styles.section}>
							<Text style={styles.label}>
								{translate("pages.driverShow.statusFS")}
							</Text>

							<IndicatorCard
								status={driver.statusFS}
								text={getFsStatusText(driver.statusFS)}
							/>
						</View>

						{includeUvv && (
							<View style={styles.section}>
								<Text style={styles.label}>
									{translate("pages.driverShow.statusUVV")}
								</Text>

								<IndicatorCard
									status={driver.statusUVV}
									text={getFsStatusText(driver.statusUVV)}
								/>
							</View>
						)}

						<View style={styles.section}>
							<Text style={styles.sectionTitle}>
								{translate("drivers.sections.licenseInfo")}
							</Text>

							<View style={styles.licenseInfoContainer}>
								{Boolean(driver.licenseIntervalDays) && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.licenseInterval")}
										</Text>

										<Text style={styles.value}>
											{Math.round(driver.licenseIntervalDays / 30)}{" "}
											{translate("drivers.fields.licenseIntervalPostfix")}
										</Text>
									</View>
								)}

								{Boolean(driver.licenseLabelCode) && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.licenseLabelCode")}
										</Text>

										<Text style={styles.value}>{driver.licenseLabelCode}</Text>
									</View>
								)}

								{driver.licenseType !== null && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.licenseType")}
										</Text>

										<Text style={styles.value}>
											{translate(`drivers.licenseType.${driver.licenseType}`)}
										</Text>
									</View>
								)}

								{Boolean(driver.licenseExpiryDate) && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.licenseExpiryDate")}
										</Text>

										<Text style={styles.value}>
											{formatIsoDate(driver.licenseExpiryDate)}
										</Text>
									</View>
								)}

								{Boolean(driver.statusFSVisualControlText) && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.statusFSVisualControlText")}
										</Text>

										<Text style={styles.value}>
											{driver.statusFSVisualControlText}
										</Text>
									</View>
								)}

								{Boolean(driver.licenseComment1) && (
									<View style={styles.infoItem}>
										<Text style={styles.label}>
											{translate("drivers.fields.licenseComment1")}
										</Text>

										<Text style={styles.value}>{driver.licenseComment1}</Text>
									</View>
								)}
							</View>

							{Boolean(driver.User) && (
								<View style={styles.section}>
									<Text style={styles.sectionTitle}>
										{translate("drivers.sections.responsible")}
									</Text>

									<Text style={styles.value}>
										{`${driver.User.firstName} ${driver.User.surName}`}
									</Text>
								</View>
							)}
						</View>
					</View>

					<View style={{ ...styles.card, width: 550 }}>
						{renderFirstPageEvents(firstPageColumns)}
					</View>
				</View>
			</Page>
			{followingPages.map((columns, index) => (
				<Page key={`events-page-${index + 2}`} orientation="landscape" size="A4">
					<View style={{ ...styles.card, width: 816 }}>
						{renderMultiColumnEvents(columns)}
					</View>
				</Page>
			))}
		</Document>
	);
};

export default DriverFilePdf;
